import { useCallback } from 'react';
import { metricsApi } from '../../../../../shared/api/metrics';
import { filterOptionsApi } from '../../../../../shared/components/Filters/api';
import type { MetricsFilters } from '../../../../../shared/api/types';

interface BreakdownEntry {
  label: string;
  value: number | null;
  rag_label?: string | null;
}

export type ProgrammeBreakdowns = Record<string, BreakdownEntry[]>;

const METRIC_KEYS = ['total_users', 'active_users', 'participation_rate', 'completion_rate'];
const METRIC_LABELS = ['Total users', 'Active users', 'Participation rate', 'Completion rate'];

export const useProgrammeBreakdowns = (baseFilterParams: MetricsFilters, clientId: string | null | undefined) => {
  const fetchBreakdowns = useCallback(async (): Promise<ProgrammeBreakdowns> => {
    if (!clientId) return {};

    const breakdowns: ProgrammeBreakdowns = {};

    // Fetch regions — skip entirely on failure
    try {
      const regions = await filterOptionsApi.getRegions(clientId);
      if (regions.data?.length) {
        const regionStats = await Promise.all(
          regions.data.map((r) =>
            metricsApi.getStats({ ...baseFilterParams, region: [r.id], metrics: METRIC_KEYS }).catch(() => null),
          ),
        );
        METRIC_KEYS.forEach((key, i) => {
          breakdowns[`${METRIC_LABELS[i]} by region`] = regions.data.map((r, ri) => ({
            label: r.name,
            value: regionStats[ri]?.data?.[key]?.value ?? null,
            rag_label: regionStats[ri]?.data?.[key]?.rag_label ?? null,
          }));
        });
      }
    } catch {
      // Region data unavailable — skip silently
    }

    // Fetch departments — skip entirely on failure
    try {
      const departments = await filterOptionsApi.getDepartments(clientId, false, false, []);
      if (departments.data?.length) {
        const deptStats = await Promise.all(
          departments.data.map((d) =>
            metricsApi.getStats({ ...baseFilterParams, department: [d.id], metrics: METRIC_KEYS }).catch(() => null),
          ),
        );
        METRIC_KEYS.forEach((key, i) => {
          breakdowns[`${METRIC_LABELS[i]} by department`] = departments.data.map((d, di) => ({
            label: d.name,
            value: deptStats[di]?.data?.[key]?.value ?? null,
            rag_label: deptStats[di]?.data?.[key]?.rag_label ?? null,
          }));
        });
      }
    } catch {
      // Department data unavailable — skip silently
    }

    return breakdowns;
  }, [baseFilterParams, clientId]);

  return { fetchBreakdowns };
};
